import { api } from "@signet/web-kit";
import type { RateUnit } from "../shared/dates.ts";

/* What the Expiries page reads from /api/m/expiries, typed once. Mirrors server/store.ts. */

export const BASE = "/api/m/expiries";

export type Category = { id: string; name: string; items: number };

export type Item = {
  id: string;
  name: string;
  category: { id: string; name: string } | null;
  rateCount: number | null;
  rateUnit: RateUnit | null;
  nextExpiry: string | null;
  price: number | null;
  currency: string;
  notes: string;
};

export type ItemPatch = Partial<{
  name: string;
  categoryId: string | null;
  rateCount: number | null;
  rateUnit: RateUnit | null;
  nextExpiry: string | null;
  price: number | null;
  currency: string;
  notes: string;
}>;

export type Payment = {
  id: string;
  itemId: string | null;
  itemName: string;
  category: string | null;
  paidOn: string;
  expiryOn: string | null;
  nextExpiry: string | null;
  amount: number;
  currency: string;
  amountAud: number | null;
  notes: string;
  itemNotes: string;
  paidBy: string | null;
};

export type Month = { month: string; aud: number; missing: number };

export const exKeys = {
  all: ["expiries"] as const,
  items: ["expiries", "items"] as const,
  upcoming: (days: number) => ["expiries", "upcoming", days] as const,
  categories: ["expiries", "categories"] as const,
  payments: ["expiries", "payments"] as const,
  monthly: ["expiries", "monthly"] as const,
};

const id = encodeURIComponent;

export const fetchItems = () => api<{ items: Item[] }>(`${BASE}/items`);
export const fetchUpcoming = (days: number) => api<{ items: Item[] }>(`${BASE}/upcoming?days=${days}`);
export const createItem = (input: ItemPatch & { name: string }) => api<{ item: Item }>(`${BASE}/items`, { body: input });
export const updateItem = (itemId: string, patch: ItemPatch) => api<{ item: Item }>(`${BASE}/items/${id(itemId)}`, { method: "PATCH", body: patch });
export const deleteItem = (itemId: string) => api(`${BASE}/items/${id(itemId)}`, { method: "DELETE" });

export const fetchCategories = () => api<{ categories: Category[] }>(`${BASE}/categories`);
export const createCategory = (name: string) => api<{ category: Category }>(`${BASE}/categories`, { body: { name } });
export const renameCategory = (categoryId: string, name: string) => api(`${BASE}/categories/${id(categoryId)}`, { method: "PATCH", body: { name } });
export const deleteCategory = (categoryId: string) => api(`${BASE}/categories/${id(categoryId)}`, { method: "DELETE" });

export const markPaid = (itemId: string, input: { amount: number; currency: string; nextExpiry: string; notes?: string }) =>
  api<{ payment: Payment }>(`${BASE}/items/${id(itemId)}/paid`, { body: input });
export const fetchPayments = () => api<{ payments: Payment[] }>(`${BASE}/payments`);
export const fetchMonthly = () => api<{ months: Month[] }>(`${BASE}/monthly`);

/** 49.5 USD as "US$49.50", in the currency's own way of writing it. */
export function price(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-AU", { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/** 12000 as "12,000.00", for a box a person types a price into. */
export const amountText = (amount: number) => amount.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "Today", "Tomorrow", "In 3 days", "2 days overdue", from Melbourne's today. */
export function whenLabel(days: number): string {
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days > 1) return `In ${days} days`;
  return days === -1 ? "1 day overdue" : `${-days} days overdue`;
}

/** "21 Oct 2026". */
export const dayText = (day: string, withYear = true) =>
  new Date(`${day}T00:00:00`).toLocaleDateString("en-AU", withYear ? { day: "numeric", month: "short", year: "numeric" } : { day: "numeric", month: "short" });
